// SignUpForm.tsx
// прямые вызовы к API авторизации, без посредников. 
// переходы в машине состояний:
// - регистрация email/passwd успешна -> WAIT_FOR_EMAIL_CONFIRMATION (вызывается onSuccess, например, для axios)
// - регистрация Google успешна -> REGISTERED (вызывается onSuccess, например, для axios)
// - регистрация неуспешна -> NOT_REGISTERED (с ошибкой)
import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputAdornment,
  IconButton,
  Link,
} from "@mui/material";
import { useTranslation } from 'react-i18next';
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { PasswordTextField } from "../userfront/PasswordTextField";
import { useAuthStore } from "../../store/useAuthStore3";

// Типы для состояния формы регистрации
interface SignUpState {
  username: string; // email
  accountName: string;
  password: string;
  passwordVerify: "",
}

interface SignUpFormProps {
  user?: string;
  accountName?: string;
  passwd?: string;
  passwordVerify?: "",
  onSuccess?: () => void; // Коллбек при успешной регистрации
}

const SignUpForm: React.FC<SignUpFormProps> = ({ user, accountName, passwd, passwordVerify, onSuccess }) => {
  const { t } = useTranslation();
  
  // Достаем методы регистрации и стейт ошибки из стора
  const { error: globalError, register, registerViaGoogle, accessToken } = useAuthStore();

  const [formData, setFormData] = useState<SignUpState>({
    username: user || "",
    password: passwd || "",
    passwordVerify: passwordVerify || "",
    accountName: accountName || "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordVerify, setShowPasswordVerify] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Сброс ошибок при размонтировании компонента (cleanup)
  useEffect(() => {
    return () => {
      useAuthStore.setState({ error: null });
    };
  }, []);

  // Если токен уже есть (пользователь авторизован), форму не рендерим
  // if (accessToken) return null;

  const handleClickShowPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShowPassword((show) => !show);
    event.currentTarget.blur();
  };

    const handleClickShowPasswordVerify = (event: React.MouseEvent<HTMLButtonElement>) => {
      setShowPasswordVerify((show) => !show);
      event.currentTarget.blur();
    }

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // 1. Стандартная регистрация (Email + Password)
  const handleSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    setIsLoading(true);
    useAuthStore.setState({ error: null }); // Очищаем старые ошибки перед новым запросом

    try {
      const res = await register(
        {
        email: formData.username,
        username: formData.accountName,
        password1: formData.password,
        password2: formData.passwordVerify,
      }
        // formData.username, formData.password
      );
      console.log('Registration succeeded', res);
      
      // Сначала вызываем коллбек родителя, пока форма не размонтировалась
      onSuccess?.();
      
      useAuthStore.setState({ email: formData.username });
      // Переводим FSM в ожидание подтверждения почты
      useAuthStore.setState({ authState: 'WAIT_FOR_EMAIL_CONFIRMATION' });
    } catch (error: any) {
      console.error('Registration failed', error);
      
      useAuthStore.setState({ email: null });
      // Переводим FSM в NOT_REGISTERED и прокидываем ошибку
      useAuthStore.setState({ 
        authState: 'NOT_REGISTERED', 
        error: error?.message || t('SignUpForm.DefaultError') 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 2. Регистрация через Google
  const handleGoogleClick = async () => {
    setIsLoading(true);
    useAuthStore.setState({ error: null });

    try {
      if (!registerViaGoogle) throw new Error("Google registration method is not implemented in store");

      // const res = await registerViaGoogle();
      // console.log('Google registration succeeded', res);
      
      const url = await registerViaGoogle();
      window.location.href = url;
      console.log('Google redirect succeeded', url);
      
      // Вызываем коллбек родителя
      // onSuccess?.();
      
      // // Переводим FSM в REGISTERED
      // useAuthStore.setState({ authState: 'REGISTERED' });
    } catch (error: any) {
      console.error('Google registration failed', error);
      
      // Переводим FSM в NOT_REGISTERED и прокидываем ошибку
      useAuthStore.setState({ 
        authState: 'NOT_REGISTERED', 
        error: error?.message || t('SignUpForm.DefaultError') 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        maxWidth: 400,
        mx: "auto",
        p: 3,
        border: "1px solid #ccc",
        borderRadius: "8px",
      }}
    >
      <Typography variant="h4" component="h1" align="center">
        {/* {t('SignUpForm.Register')} */}
        Зареєструватись
      </Typography>
      
      {globalError && (
        <Typography color="error" align="center" sx={{ fontWeight: "bold" }}>
          {globalError}
        </Typography>
      )}      
      
      <FormControl fullWidth>
        <TextField
          // label={t('SignUpForm.Email')}
          label={'Email'}
          name="username"
          type="text"
          value={formData.username}
          onChange={handleInputChange}
          disabled={isLoading}
          required
        />
      </FormControl>

      <FormControl fullWidth>
        <TextField
          label={'Account Name'}
          name="accountName"
          type="text"
          value={formData.accountName}
          onChange={handleInputChange}
          disabled={isLoading}
          required
        />
      </FormControl>

      <FormControl fullWidth>
        <PasswordTextField
          // label={t('SignUpForm.Password')}
          label={'Password'}
          name="password"
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={handleInputChange}
          disabled={isLoading}
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  disabled={isLoading}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </FormControl>

      <FormControl fullWidth>
        <PasswordTextField
          label="Verify password"
          // label={t('SignupForm.VerifyPassword')}
          name="passwordVerify"
          // Тип поля меняется в зависимости от showPasswordVerify
          type={showPasswordVerify ? "text" : "password"}
          value={formData.passwordVerify}
          onChange={handleInputChange}
          required
          variant="outlined"
          // Добавляем иконку
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClickShowPasswordVerify}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPasswordVerify ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </FormControl>

      <Button 
        type="submit" 
        variant="contained" 
        disabled={isLoading}
      >
        {/* {isLoading ? t('SignUpForm.Loading') : t('SignUpForm.Register')} */}
        Зареєструватись
      </Button>

      <Button 
        variant="outlined" 
        onClick={handleGoogleClick}
        disabled={isLoading}
        fullWidth
      >
        Зареєструватись через Google
      </Button>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => useAuthStore.setState({ authState: 'LOGIN' })}
          disabled={isLoading}
          underline="hover"
        >
          {'Якшо вже зареєстровані, перейдіть до логіну'}
        </Link>
      </Box>

    </Box>
  );
};

export default SignUpForm;